import styles from '../styles/styles.module.css';
import { usePreciseNow } from '../store/now';
import { getCanthaTime, formatCanthaTime, getCanthaStageLabel, getCanthaGradientPosition, type CanthaStage } from '../utils/time';
import { IcoDawn, IcoDay, IcoDusk, IcoNight, IcoClose, IcoWiki } from './IcoComponents';
import { useSettings } from '../store/settings';
import { Tooltip } from 'react-tooltip';
import type React from 'react';

const STAGE_ICONS: Record<CanthaStage, () => React.ReactElement> = {
  dawn: IcoDawn,
  day: IcoDay,
  dusk: IcoDusk,
  night: IcoNight,
};

export function CanthaClockDisplay() {
  const now = usePreciseNow();
  const { toggleDayNightTracker } = useSettings();
  const canthaTime = getCanthaTime(now);
  const StageIcon = STAGE_ICONS[canthaTime.stage];
  const stageLabel = getCanthaStageLabel(canthaTime.stage);
  const timeString = formatCanthaTime(canthaTime);
  
  // Calculate gradient scroll position for infinite scrolling
  // Each gradient instance is 100% wide and represents one full in-game day (00:00-24:00)
  // Gradient starts at 00:00 (night) at 0% position
  // We need multiple instances for seamless infinite scroll
  // Use transform for better precision and performance to avoid gaps
  const gradientPositionPercent = getCanthaGradientPosition(now);
  // Position the current day's gradient so the current time point is at the center (50% of container)
  // Use transform translateX for pixel-perfect positioning
  const currentDayOffset = 50 - gradientPositionPercent;
  // Create multiple instances to ensure seamless coverage
  const gradientInstances = [
    { offset: currentDayOffset - 100, key: 'prev' },
    { offset: currentDayOffset, key: 'current' },
    { offset: currentDayOffset + 100, key: 'next' },
  ];

  return (
    <div className="relative flex flex-col gap-3 items-center justify-center py-4 px-6 bg-[var(--color-bg-secondary)] rounded-[9px] border border-[var(--color-fg-secondary)] w-full">
      {/* Close button */}
      <button
        onClick={() => toggleDayNightTracker('cantha')}
        className="absolute top-2 right-2 z-20 flex items-center justify-center text-[var(--color-text-muted)] hover:text-[var(--color-text-default)] cursor-pointer"
        aria-label="Close Canthan clock"
      >
        <IcoClose />
      </button>
      {/* Title with tooltip */}
      <div className="flex items-center gap-2">
        <span className={`${styles.newsreaderh1Medium} text-[var(--color-text-muted)]`}>
          Canthan Time
        </span>
        <div
          data-tooltip-id="cantha-clock-tooltip"
          className="flex items-center justify-center text-[var(--color-text-muted)] hover:text-[var(--color-text-default)] cursor-help"
        >
          <IcoWiki />
        </div>
        <Tooltip
          id="cantha-clock-tooltip"
          border="1px solid var(--color-fg-secondary)"
          className="z-9999"
          place="top"
          delayShow={300}
          delayHide={100}
          offset={8}
          style={{
            backgroundColor: 'var(--color-fg-secondary)',
            color: 'var(--color-text-emphasis)',
            fontSize: '12px',
            fontFamily: 'var(--font-family-inconsolata)',
            padding: '6px 8px 4px 8px',
            boxShadow: 'var(--shadow-button)',
            maxWidth: '200px',
          }}
        >
          <div className="flex flex-col gap-1">
            <div>Dawn: 07:00 – 08:00</div>
            <div>Day: 08:00 – 19:00</div>
            <div>Dusk: 19:00 – 20:00</div>
            <div>Night: 20:00 – 07:00</div>
          </div>
        </Tooltip>
      </div>

      {/* Dynamic text with icon */}
      <div className="flex items-center gap-2">
        <StageIcon />
        <span className={`${styles.inconsolataBodyXS} text-[var(--color-text-default)] font-medium`}>
          {stageLabel}
        </span>
      </div>

      {/* Timeline with gradient */}
      <div className="relative w-full h-8 rounded overflow-hidden border-[var(--color-fg-secondary)] border">
        {/* Static underlay with night color to fill any gaps */}
        <div
          className="absolute inset-0 rounded-[9px]"
          style={{
            backgroundColor: '#040B3C',
          }}
        />
        {/* Gradient instances for seamless infinite scroll */}
        {/* Each gradient is 100% wide and represents one full in-game day (00:00-24:00) */}
        {/* Using transform for pixel-perfect positioning */}
        {gradientInstances.map((instance) => (
          <div
            key={instance.key}
            className="absolute top-0 bottom-0"
            style={{
              width: '100%',
              height: '100%',
              background: 'linear-gradient(90deg, #040B3C 0%, #040B3C 6%, #004372 11%, #004372 17%, #016792 23%, #07729F 26%, #12A1C0 27%, #74D4CC 29%, #EFEEBC 32%, #FEE154 38%, #FDC352 43%, #FFAC6F 49%, #FDA65A 55%, #FD9E58 61%, #F18448 66%, #F06B7E 72%, #CA5A92 74%, #5B2C83 75%, #371A79 77%, #28166B 79%, #192861 84%, #040B3C 89%, #040B3C 100%)',
              left: 0,
              transform: `translateX(${instance.offset}%)`,
              willChange: 'transform',
            }}
          />
        ))}
        {/* Static vertical line indicator in center with triangles at both ends */}
        <div
          className="absolute top-0 bottom-0 left-1/2 pointer-events-none z-10 mix-blend-difference"
          style={{
            transform: 'translateX(-50%)',
          }}
        >
          {/* Top triangle */}
          <div
            className="absolute top-0 left-1/2"
            style={{
              transform: 'translateX(-50%)',
              width: 0,
              height: 0,
              borderLeft: '4px solid transparent',
              borderRight: '4px solid transparent',
              borderTop: '4px solid rgba(255, 255, 255, 0.75)',
            }}
          />
          {/* Vertical line */}
          <div
            className="absolute top-1 left-1/2 bottom-1 w-px bg-white/80"
            style={{
              transform: 'translateX(-50%)',
            }}
          />
          {/* Bottom triangle */}
          <div
            className="absolute bottom-0 left-1/2"
            style={{
              transform: 'translateX(-50%)',
              width: 0,
              height: 0,
              borderLeft: '4px solid transparent',
              borderRight: '4px solid transparent',
              borderBottom: '4px solid rgba(255, 255, 255, 0.75)',
            }}
          />
        </div>
      </div>

      {/* Current game time */}
      <span className={`${styles.inconsolataBodyXS} text-[var(--color-text-default)] font-medium`}>
        {timeString}
      </span>
    </div>
  );
}

